import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect, useRef, Suspense } from "react";
import { Home, ScanLine, ShoppingCart, Truck, Store, Globe, Bell, Menu,
  KeyRound, Settings as SettingsIcon, ReceiptText, LogOut, Users, Activity, Tag,
  Gift, MessageCircle, FileText, Copy, Share2, CreditCard, ChevronRight, History, Mail, Info, Crown, Check, X, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useI18n, LANGS } from "@/lib/i18n";
import { api } from "@/lib/api";
import { ZapCartLogo } from "@/components/ZapCartLogo";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AboutModal } from "@/components/AboutModal";
import { StoreSidebar } from "@/components/StoreSidebar";
import { PasskeyPrompt } from "@/components/PasskeyPrompt";

const SUPPORT_WA = "447700900000"; // Help & Support WhatsApp
const SUPPORT_EMAIL = "support@zapcart.co.uk"; // Help & Support email

const NAV = [
  { to: "/dashboard", k: "home", icon: Home, testid: "nav-home" },
  { to: "/suppliers", k: "cart", icon: ShoppingCart, testid: "nav-cart" },
  { to: "/cashcarry", k: "depot", icon: Truck, testid: "nav-cashcarry" },
  { to: "/history", k: "history", icon: History, testid: "nav-history" },
];

export default function AppShell() {
  const { user, activeStaff, exitPin, logout } = useAuth();
  const { t, lang, setLang } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const isManager = user?.role === "owner" || user?.role === "manager";
  const scanActive = location.pathname === "/scanner";

  // Cut-off push reminders: browser notification at 60 & 30 mins before a supplier's
  // deadline when that supplier still has draft items (in-app + Notification API).
  const firedRef = useRef({});
  useEffect(() => {
    if (!isManager) return;
    if (typeof Notification !== "undefined" && Notification.permission === "default") { try { Notification.requestPermission(); } catch { /* ignore */ } }
    const check = async () => {
      try {
        const { data } = await api.get("/suppliers/deadlines");
        for (const d of data) {
          if (!d.draft_count) continue;
          const mins = d.seconds_left / 60;
          for (const th of [60, 30]) {
            const key = `${d.name}-${th}`;
            if (mins <= th && mins > th - 5 && !firedRef.current[key]) {
              firedRef.current[key] = true;
              const msg = `${d.name} order cut-off in ~${th} mins — ${d.draft_count} case(s) still in your draft basket.`;
              toast.warning(msg, { duration: 6000 });
              try { if (typeof Notification !== "undefined" && Notification.permission === "granted") new Notification("⏰ ZapBasket cut-off reminder", { body: msg }); } catch { /* ignore */ }
            }
          }
        }
      } catch { /* ignore */ }
    };
    check(); const iv = setInterval(check, 60000);
    return () => clearInterval(iv);
  }, [isManager]);

  return (
    <div className="min-h-screen bg-white text-slate-900 pb-32 lg:pb-12 lg:pl-64">
      <StoreSidebar />
      <header className="sticky top-0 z-30 bg-white/90 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-3xl lg:max-w-[1600px] mx-auto px-4 lg:px-8 h-14 lg:h-16 flex items-center justify-between">
          <div className="flex items-center gap-2 lg:hidden">
            <SidebarDrawer isManager={isManager} isAdmin={!!user?.is_platform_admin} navigate={navigate} exitPin={exitPin} logout={logout} user={user} activeStaff={activeStaff} />
            <ZapCartLogo size={30} />
            <div className="leading-tight">
              <p className="font-extrabold text-slate-900 text-sm font-head">Zap<span className="text-emerald-600">Basket</span></p>
              <p data-no-translate className="text-[10px] text-slate-400 -mt-0.5 truncate max-w-[130px]" data-testid="header-shop-name">{user?.store_name}</p>
            </div>
          </div>
          <p data-no-translate className="hidden lg:block text-sm font-bold text-slate-700 truncate" data-testid="header-shop-name-desktop">{user?.store_name}</p>
          <div className="flex items-center gap-1.5">
            {activeStaff && (
              <button data-testid="active-staff-chip" onClick={() => { exitPin(); navigate("/staff"); }}
                className="flex items-center gap-1.5 h-9 pl-1 pr-3 rounded-full bg-slate-100 active:scale-95">
                <span className="w-6 h-6 rounded-full flex items-center justify-center text-white text-[11px] font-bold" style={{ background: activeStaff.color || "#2563EB" }}>{activeStaff.name?.[0]}</span>
                <span data-no-translate className="text-xs font-semibold text-slate-700 hidden sm:inline">{activeStaff.name}</span>
              </button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild><button data-testid="lang-switcher-btn" className="w-9 h-9 rounded-full hover:bg-slate-100 flex items-center justify-center"><Globe className="w-5 h-5 text-slate-600" /></button></DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <div className="px-2 py-1.5 text-xs text-slate-500">{t("language")}</div>
                <DropdownMenuSeparator />
                {LANGS.map((l) => <DropdownMenuItem key={l.code} data-testid={`lang-${l.code}`} onClick={() => setLang(l.code)} className={lang === l.code ? "font-bold text-emerald-600" : ""}>{l.label}</DropdownMenuItem>)}
              </DropdownMenuContent>
            </DropdownMenu>
            {isManager && <NotificationBell navigate={navigate} />}
          </div>
        </div>
      </header>

      <main className="max-w-3xl lg:max-w-[1600px] mx-auto px-4 lg:px-8 py-5 lg:py-8"><Suspense fallback={<div data-testid="page-loader" className="space-y-3 pt-2">{[0, 1, 2].map((i) => <div key={i} className="h-16 rounded-2xl bg-slate-100 animate-pulse" />)}</div>}><Outlet /></Suspense></main>
      <PasskeyPrompt />

      <nav data-testid="mobile-bottom-nav" className="lg:hidden fixed bottom-0 left-0 right-0 z-40">
        <div className="max-w-3xl mx-auto relative">
          <div className="bg-white border-t border-slate-200 card-soft grid grid-cols-5 items-end px-2">
            {NAV.slice(0, 2).map((n) => <NavBtn key={n.to} n={n} active={location.pathname === n.to} onClick={() => navigate(n.to)} />)}
            <div className="flex justify-center">
              <button data-testid="floating-scan-btn" onClick={() => navigate("/scanner")}
                className={`-mt-7 w-16 h-16 rounded-full flex items-center justify-center text-white shadow-xl transition-all active:scale-90 ${scanActive ? "bg-emerald-500" : "bg-[#1D4ED8]"}`}
                style={{ boxShadow: "0 8px 24px rgba(29,78,216,0.4)" }}>
                <ScanLine className="w-7 h-7" strokeWidth={2.5} />
              </button>
            </div>
            {NAV.slice(2).map((n) => <NavBtn key={n.to} n={n} active={location.pathname === n.to} onClick={() => navigate(n.to)} />)}
          </div>
        </div>
      </nav>
    </div>
  );
}

const NavBtn = ({ n, active, onClick }) => {
  const Icon = n.icon;
  const { t } = useI18n();
  return (
    <button data-testid={n.testid} onClick={onClick} className={`flex flex-col items-center gap-1 py-3 ${active ? "text-[#1D4ED8]" : "text-slate-400"}`}>
      <Icon className="w-5 h-5" strokeWidth={active ? 2.5 : 2} /><span className="text-[10px] font-semibold">{t(n.k)}</span>
    </button>
  );
};

const DrawerRow = ({ icon: Icon, label, onClick, testid, tone }) => (
  <button data-testid={testid} onClick={onClick} className="w-full flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-slate-50 active:scale-[0.99] transition-all text-left">
    <span className={`w-9 h-9 rounded-xl flex items-center justify-center ${tone || "bg-slate-100 text-slate-600"}`}><Icon className="w-4 h-4" /></span>
    <span className="flex-1 text-sm font-semibold text-slate-800">{label}</span>
    <ChevronRight className="w-4 h-4 text-slate-300" />
  </button>
);

const SidebarDrawer = ({ isManager, navigate, exitPin, logout, user, activeStaff }) => {
  const [open, setOpen] = useState(false);
  const go = (to) => { setOpen(false); navigate(to); };
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button data-testid="drawer-menu-btn" className="w-9 h-9 -ml-1 rounded-full hover:bg-slate-100 flex items-center justify-center"><Menu className="w-5 h-5 text-slate-700" /></button>
      </SheetTrigger>
      <SheetContent side={document.documentElement.dir === "rtl" ? "right" : "left"} className="w-[300px] p-0 flex flex-col [&>button]:text-white [&>button]:top-3 [&>button]:end-3 [&>button]:start-auto" data-testid="sidebar-drawer">
        <SheetTitle className="sr-only">Menu</SheetTitle>
        <div className="p-4 bg-gradient-to-br from-[#1D4ED8] to-[#2563EB] text-white">
          <div className="flex items-center gap-2"><ZapCartLogo size={30} /><p className="font-extrabold font-head">ZapBasket</p></div>
          <p data-no-translate className="text-sm font-semibold mt-3">{user?.store_name}</p>
          <p data-no-translate className="text-xs text-blue-100">{activeStaff?.name || "Store"}</p>
        </div>
        <div className="flex-1 overflow-y-auto p-2 space-y-0.5">
          {isManager && <>
            <DrawerRow testid="drawer-billing" icon={CreditCard} label="Account & Billing" onClick={() => go("/settings")} tone="bg-blue-50 text-blue-600" />
            <DrawerRow testid="drawer-team" icon={Users} label="Team Profiles & Manager PIN" onClick={() => go("/team")} />
            <DrawerRow testid="drawer-vault" icon={KeyRound} label="Manage Wholesaler Vault" onClick={() => go("/supplier-setup")} />
            <DrawerRow testid="drawer-referral" icon={Gift} label="Invite & Earn — 20 days free" onClick={() => go("/referral")} tone="bg-emerald-50 text-emerald-600" />
          </>}
          <DrawerRow testid="drawer-about" icon={Info} label="About ZapBasket & How It Works" onClick={() => go("/about")} tone="bg-blue-50 text-blue-600" />
          <DrawerRow testid="drawer-help" icon={MessageCircle} label="Help & Support" onClick={() => go("/help")} tone="bg-green-50 text-green-600" />
          <div className="h-px bg-slate-100 my-1" />
          <DrawerRow testid="drawer-switch-staff" icon={Users} label="Switch staff profile" onClick={() => { setOpen(false); exitPin(); navigate("/staff"); }} />
          <DrawerRow testid="drawer-logout" icon={LogOut} label="Log out of store" onClick={() => { setOpen(false); logout(); navigate("/auth"); }} tone="bg-red-50 text-red-600" />
        </div>
      </SheetContent>
    </Sheet>
  );
};

const STORE_AISLES = ["Drinks & Beverages", "Crisps & Snacking", "Chilled & Fresh", "Bakery & Grocery", "Frozen", "Wines"];

const NotificationBell = ({ navigate }) => {
  const [data, setData] = useState({ items: [], unread: 0 });
  const [open, setOpen] = useState(false);
  const [priceFor, setPriceFor] = useState(null); // notif id being priced
  const [retail, setRetail] = useState("");
  const [cost, setCost] = useState("");
  const [cat, setCat] = useState("");
  const load = () => api.get("/notifications").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); const iv = setInterval(load, 20000); return () => clearInterval(iv); }, []);
  const markAll = async () => { try { await api.post("/notifications/read-all"); setData((d) => ({ ...d, unread: 0 })); } catch { /* ignore */ } };
  const markRead = async (n) => { if (n.read) return; try { await api.post(`/notifications/${n.id}/read`); setData((d) => ({ items: d.items.map((x) => x.id === n.id ? { ...x, read: true } : x), unread: Math.max(0, d.unread - 1) })); } catch { /* ignore */ } };
  const dismiss = async (n) => { try { await api.delete(`/notifications/${n.id}`); setData((d) => ({ items: d.items.filter((x) => x.id !== n.id), unread: Math.max(0, d.unread - (n.read ? 0 : 1)) })); if (priceFor === n.id) setPriceFor(null); toast.success("Notification cleared"); } catch { /* ignore */ } };
  const openPrice = (n) => { setPriceFor(priceFor === n.id ? null : n.id); setRetail(""); setCost(n.meta?.price ? String(n.meta.price) : ""); setCat(n.meta?.category || ""); };
  const savePrice = async (n) => {
    const r = parseFloat(retail); if (isNaN(r) || r <= 0) return toast.error("Enter a retail price");
    try { const { data: res } = await api.post(`/notifications/${n.id}/approve`, { retail_price: r, unit_cost: cost ? parseFloat(cost) : undefined, category: cat || undefined });
      toast.success(`Added to catalogue • ${res.category} • £${r.toFixed(2)}`); setPriceFor(null);
      setData((d) => ({ items: d.items.filter((x) => x.id !== n.id), unread: Math.max(0, d.unread - (n.read ? 0 : 1)) }));
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not set price"); }
  };
  const goCredit = (n) => { setOpen(false); if (n.meta?.order_id) navigate(`/history/${n.meta.order_id}`); };
  const margin = retail && cost ? Math.round((parseFloat(retail) - parseFloat(cost)) / parseFloat(retail) * 100) : null;

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button data-testid="notif-bell-btn" className="relative w-9 h-9 rounded-full hover:bg-slate-100 flex items-center justify-center">
          <Bell className="w-5 h-5 text-slate-600" />
          {data.unread > 0 && <span data-testid="notif-unread" className="absolute -top-0.5 -right-0.5 min-w-4 h-4 px-1 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center">{data.unread}</span>}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={8} className="w-[20rem] p-0">
        <div className="flex items-center justify-between px-3 py-2">
          <span className="text-sm font-bold text-slate-800">Manager Inbox</span>
          {data.unread > 0 && <button data-testid="notif-mark-all" onClick={markAll} className="text-[11px] font-bold text-indigo-600 hover:underline">Mark all as read</button>}
        </div>
        <DropdownMenuSeparator className="my-0" />
        <div className="max-h-96 overflow-y-auto">
          {data.items.length === 0 && <p className="text-xs text-slate-400 text-center py-6">No notifications yet.</p>}
          {data.items.map((n) => (
            <div key={n.id} data-testid={`notif-${n.id}`} onClick={() => markRead(n)}
              className={`px-3 py-2.5 border-b border-slate-50 last:border-0 ${n.read ? "" : "bg-indigo-50/40"}`}>
              <div className="flex items-start gap-2">
                {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 mt-1.5 shrink-0" />}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-800 leading-tight">{n.title}</p>
                  {n.detail && <p className="text-[11px] text-slate-500 mt-0.5">{n.detail.replace(/^£0(\.0+)?(?= ·)/, "Price not set")}</p>}

                  {n.kind === "catalogue" && (
                    <div className="flex gap-1.5 mt-2" onClick={(e) => e.stopPropagation()}>
                      <button data-testid={`notif-approve-${n.id}`} onClick={() => openPrice(n)} className="flex items-center gap-1 text-[11px] font-bold px-2.5 py-1.5 rounded-lg bg-emerald-600 text-white active:scale-95"><Tag className="w-3 h-3" /> Approve & Add to Catalogue</button>
                      <button data-testid={`notif-dismiss-${n.id}`} onClick={() => dismiss(n)} className="flex items-center gap-1 text-[11px] font-bold px-2.5 py-1.5 rounded-lg bg-slate-100 text-slate-600 active:scale-95"><Trash2 className="w-3 h-3" /> Dismiss</button>
                    </div>
                  )}
                  {priceFor === n.id && (
                    <div data-testid={`notif-price-panel-${n.id}`} className="mt-2 p-2 rounded-xl bg-white border border-emerald-200" onClick={(e) => e.stopPropagation()}>
                      <div className="flex gap-2">
                        <div className="flex-1"><label className="text-[9px] font-bold uppercase text-slate-400">Cost £</label><Input data-testid={`notif-cost-${n.id}`} type="number" step="0.01" value={cost} onChange={(e) => setCost(e.target.value)} className="h-9 mt-0.5" /></div>
                        <div className="flex-1"><label className="text-[9px] font-bold uppercase text-slate-400">Retail £</label><Input data-testid={`notif-retail-${n.id}`} type="number" step="0.01" value={retail} onChange={(e) => setRetail(e.target.value)} className="h-9 mt-0.5" /></div>
                      </div>
                      <p data-testid={`notif-por-${n.id}`} className="text-[10px] font-bold text-indigo-600 mt-1">{margin != null && isFinite(margin) ? `Margin / POR: ${margin}%` : "Add a cost price to see POR"}</p>
                      <label className="text-[9px] font-bold uppercase text-slate-400 block mt-1.5">Category</label>
                      <select data-testid={`notif-category-${n.id}`} value={cat} onChange={(e) => setCat(e.target.value)} className="w-full h-9 mt-0.5 rounded-md border border-slate-200 bg-white px-2 text-xs font-semibold text-slate-700">
                        <option value="">Auto-detect</option>
                        {STORE_AISLES.map((a) => <option key={a} value={a}>{a}</option>)}
                      </select>
                      <Button data-testid={`notif-price-save-${n.id}`} onClick={() => savePrice(n)} className="w-full h-9 mt-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs gap-1"><Check className="w-3.5 h-3.5" /> Approve & Add to Catalogue</Button>
                    </div>
                  )}

                  {n.kind === "grn" && n.meta?.order_id && (
                    <button data-testid={`notif-credit-${n.id}`} onClick={(e) => { e.stopPropagation(); goCredit(n); }} className="flex items-center gap-1 mt-2 text-[11px] font-bold px-2.5 py-1.5 rounded-lg bg-amber-500 text-white active:scale-95">View order details <ChevronRight className="w-3 h-3" /></button>
                  )}
                </div>
                {n.kind !== "catalogue" && (
                  <button data-testid={`notif-x-${n.id}`} onClick={(e) => { e.stopPropagation(); dismiss(n); }} className="w-6 h-6 rounded-md hover:bg-slate-100 flex items-center justify-center shrink-0"><X className="w-3.5 h-3.5 text-slate-400" /></button>
                )}
              </div>
            </div>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
